import { debounce, isMobile, domBatcher, prefersReducedMotion, onFirstUserPresenceIdle, checkIsAboveFold } from "./utilities-ClmlCyst.js";
import { p as prepareEmblaInteraction, d as debouncedWatchResize, H as HybridCarousel, b as duplicateSlidesForLoop } from "./embla-extension-BBVpnNKd.js";
import { EmblaMarqueeController } from "./embla-marquee-controller-DNgoYbzI.js";
import { A as Autoplay } from "./embla-carousel-autoplay.esm-lYa57aZD.js";
import { isMobileAnim } from "./mobile-anim-gate-Btn1wVuD.js";
import { unregisterSentryFlush } from "./anim-batch-nQ-NAxq8.js";
import { flattenListingLayout, getListingRevealController, scheduleDesignModeListingInit, cacheListingEmblaNodes, parseLiquidJSON, setupListingEmblaFeatures, enableListingNavigation, enableListingDots, applyListingGridLayout, destroyListingEmbla, trimListingLoopClones, canSetupListingReveal, ensureListingRevealReadyForPlay } from "./listing-component-shared-Bco1AtVo.js";
import { w as waapi } from "./waapi-patch-BCt4pEht.js";
import "./cookie-banner-layout-DZP7Q0uT.js";
import "./section-anchor-scroll-Bvm-mXVl.js";
import "./native-scroll-snap-Cd15iSfl.js";
import "./mobile-autoplay-LQAitT2O.js";
import "./progressive-images-mIwHxxsh.js";
import "./autoplay-progress-BLL6tdZq.js";
class ListingComponent extends HTMLElement {
  constructor() {
    super();
    this.emblaApi = null;
    this.originalSlideCount = 0;
    this.cleanupFunctions = [];
    this.currentDeviceType = null;
    this._isFirstInit = true;
    this.marquee = null;
    this._editorMarqueePaused = false;
    this.nodes = {
      embla: null,
      viewport: null,
      container: null,
      prevBtns: [],
      nextBtns: [],
      dots: null
    };
    this.config = {
      showNavigation: false,
      showDots: false,
      options: {},
      autoScroll: {
        desktop: false,
        mobile: false
      },
      autoPlay: {
        desktop: false,
        mobile: false
      }
    };
    this.gridConfig = {
      desktop: { rows: 1, itemsPerRow: 4 },
      mobile: { rows: 1, itemsPerRow: 1 }
    };
    this.handleResize = debounce(() => this.onResize(), 300);
    this._reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );
    this._onMotionChange = () => this.init();
  }
  isInteractionReady() {
    var _a, _b;
    return ((_a = this.marquee) == null ? void 0 : _a.isInteractionReady()) || !!this.emblaApi || !((_b = this.nodes) == null ? void 0 : _b.viewport);
  }
  prepareInteraction() {
    var _a, _b, _c;
    if (Shopify.designMode && !this.isInteractionReady()) {
      this.init();
      (_a = this.marquee) == null ? void 0 : _a.prepareInteraction();
      return this.isInteractionReady();
    }
    if (this._shouldUseMarquee()) {
      if (!((_b = this.marquee) == null ? void 0 : _b.isActive)) this.init();
      return ((_c = this.marquee) == null ? void 0 : _c.prepareInteraction()) || false;
    }
    return prepareEmblaInteraction(this);
  }
  connectedCallback() {
    var _a;
    this.cacheNodes();
    this.ssrRows = Number(this.dataset.ssrRows) || 1;
    this._domRows = this.ssrRows;
    this.gridConfig = {
      desktop: {
        rows: Number(this.dataset.rowsDesktop) || 1,
        itemsPerRow: Number(this.dataset.itemsPerRowDesktop) || 4
      },
      mobile: {
        rows: Number(this.dataset.rowsMobile) || 1,
        itemsPerRow: Number(this.dataset.itemsPerRowMobile) || 1
      }
    };
    const container = this.nodes.container;
    if (container) {
      const directChildren = Array.from(container.children);
      const hasDirectItems = Shopify.designMode && directChildren.some(
        (element) => !element.classList.contains("embla__slide")
      );
      if (hasDirectItems) {
        this.originalItems = directChildren;
        flattenListingLayout(this);
        this._domRows = 1;
      } else {
        this.originalItems = Array.from(
          container.querySelectorAll(":scope > .embla__slide > *")
        );
      }
    } else {
      this.originalItems = [];
    }
    this.parseConfig();
    this.currentDeviceType = isMobile() ? "mobile" : "desktop";
    const controller = getListingRevealController(this);
    this._controller = controller;
    if (!controller && !Shopify.designMode) {
      this._initStandalone();
    } else if (controller) {
      const isDesktopActive = controller.classList.contains("active");
      const isMobileActive = (_a = controller.closest(".megamenu-mobile-container")) == null ? void 0 : _a.classList.contains("is-megamenu-active");
      if (controller.tagName.toLowerCase() !== "slideshow-component" && (isDesktopActive || isMobileActive)) {
        if (controller.classList.contains("megamenu-v2")) {
          const token = this._tabSetupToken;
          domBatcher.write(() => {
            var _a2;
            if (this._tabSetupToken === token) (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
        } else {
          const token = this._tabSetupToken;
          requestAnimationFrame(() => {
            var _a2;
            if (this._tabSetupToken === token) (_a2 = this.reset) == null ? void 0 : _a2.call(this);
          });
        }
      } else if ([
        "custom-dialog",
        "cart-drawer",
        "quick-view-dialog",
        "custom-div-popup"
      ].includes(controller.tagName.toLowerCase())) {
        const dialog = controller.querySelector("dialog");
        if (dialog && dialog.open) {
          domBatcher.write(() => {
            var _a2;
            return (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
        }
        const openHandler = () => domBatcher.write(() => {
          var _a2;
          return (_a2 = this.play) == null ? void 0 : _a2.call(this);
        });
        const closeHandler = () => domBatcher.write(() => {
          var _a2;
          return (_a2 = this.reset) == null ? void 0 : _a2.call(this);
        });
        controller.addEventListener("dialog:open", openHandler);
        controller.addEventListener("dialog:closing", closeHandler);
        if (typeof this.registerCleanup === "function") {
          this.registerCleanup(() => {
            controller.removeEventListener("dialog:open", openHandler);
            controller.removeEventListener("dialog:closing", closeHandler);
          });
        }
      }
    }
    if (Shopify.designMode) {
      scheduleDesignModeListingInit(this);
    }
    window.addEventListener("resize", this.handleResize);
    this._reducedMotionQuery.addEventListener("change", this._onMotionChange);
  }
  disconnectedCallback() {
    var _a, _b, _c;
    window.removeEventListener("resize", this.handleResize);
    this._reducedMotionQuery.removeEventListener(
      "change",
      this._onMotionChange
    );
    (_a = this._foldIO) == null ? void 0 : _a.disconnect();
    (_b = this._scrollObs) == null ? void 0 : _b.disconnect();
    if (this._introTrigger) {
      document.removeEventListener("theme:intro:complete", this._introTrigger);
      this._introTrigger = null;
    }
    if (this._sentryCallback) {
      unregisterSentryFlush(this._sentryCallback);
      this._sentryCallback = null;
    }
    (_c = this.reset) == null ? void 0 : _c.call(this);
    this.destroy();
  }
  // ========== DOM & Configuration ==========
  cacheNodes() {
    cacheListingEmblaNodes(this, {
      scopeToEmbla: true,
      excludeNestedViewports: false
    });
  }
  parseConfig() {
    this.config.showNavigation = this.dataset.showNavigation === "true";
    this.config.showDots = this.dataset.showDots === "true";
    this.config.options = this.parseJSON(this.dataset.options, {});
    const autoScrollDesktop = this.parseJSON(
      this.dataset.autoScrollDesktop,
      false
    );
    const autoScrollMobile = this.parseJSON(
      this.dataset.autoScrollMobile,
      false
    );
    this.config.autoScroll.desktop = autoScrollDesktop || false;
    this.config.autoScroll.mobile = autoScrollMobile || autoScrollDesktop;
    const autoPlayDesktop = this.parseJSON(this.dataset.autoPlayDesktop, false);
    const autoPlayMobile = this.parseJSON(this.dataset.autoPlayMobile, false);
    this.config.autoPlay.desktop = autoPlayDesktop || false;
    this.config.autoPlay.mobile = autoPlayMobile || autoPlayDesktop;
  }
  parseJSON(jsonString, fallback) {
    return parseLiquidJSON(jsonString, fallback, "ListingComponent");
  }
  getCurrentAutoScrollConfig() {
    return isMobile() ? this.config.autoScroll.mobile : this.config.autoScroll.desktop;
  }
  getCurrentAutoPlayConfig() {
    return isMobile() ? this.config.autoPlay.mobile : this.config.autoPlay.desktop;
  }
  getCurrentGridConfig() {
    return isMobile() ? this.gridConfig.mobile : this.gridConfig.desktop;
  }
  _shouldUseMarquee() {
    return !!this.getCurrentAutoScrollConfig() && !prefersReducedMotion();
  }
  // ========== Initialization (Router) ==========
  init() {
    this.destroy();
    const currentRows = this.getCurrentGridConfig().rows;
    if (this._domRows !== currentRows) {
      currentRows > 1 ? this.applyGridLayout() : this.flattenLayout();
      this._domRows = currentRows;
    }
    if (this._shouldUseMarquee()) {
      this.initMarquee(this.getCurrentAutoScrollConfig());
    } else {
      this.initEmbla();
    }
    this._isFirstInit = false;
  }
  // ========== Embla Mode ==========
  initEmbla() {
    if (Shopify.designMode) {
      this._doInitEmbla();
    } else {
      if (this._emblaInitQueued) return;
      this._emblaInitQueued = true;
      onFirstUserPresenceIdle(() => {
        this._emblaInitQueued = false;
        if (!this.isConnected) return;
        this._doInitEmbla();
      });
    }
  }
  _doInitEmbla() {
    if (!this.nodes.viewport) return;
    if (this._shouldUseMarquee()) return;
    const autoPlayConfig = this.getCurrentAutoPlayConfig();
    const plugins = [];
    if (autoPlayConfig) {
      plugins.push(
        Autoplay({
          delay: autoPlayConfig.delay ?? 4e3,
          stopOnInteraction: autoPlayConfig.stopOnInteraction ?? false,
          stopOnMouseEnter: autoPlayConfig.stopOnMouseEnter ?? true,
          stopOnFocusIn: false
        })
      );
    }
    try {
      const options = {
        ...this.config.options,
        watchResize: debouncedWatchResize()
      };
      if (prefersReducedMotion() && this.getCurrentAutoScrollConfig()) {
        options.watchDrag = true;
        if (options.breakpoints) {
          for (const bp in options.breakpoints) {
            options.breakpoints[bp].watchDrag = true;
          }
        }
      }
      if (this.nodes.container) {
        this.nodes.container.querySelectorAll("[data-embla-cloned]").forEach((el) => el.remove());
      }
      this.emblaApi = HybridCarousel(this.nodes.viewport, options, plugins);
      this.originalSlideCount = 0;
      if (this.emblaApi.internalEngine().options.loop) {
        this.originalSlideCount = duplicateSlidesForLoop(
          this.emblaApi,
          this.nodes.container,
          5,
          (clone) => this._resetClonedVideos(clone)
        );
      }
      this.featureCleanups = [];
      this.setupEmblaFeatures();
      this.emblaApi.on("reInit", () => this.setupEmblaFeatures());
    } catch (error) {
    }
  }
  setupEmblaFeatures() {
    setupListingEmblaFeatures(this);
  }
  enableNavigation() {
    enableListingNavigation(this);
  }
  enableDots() {
    enableListingDots(this, { useOriginalSlideCount: true });
  }
  // ========== Public API (Theme Editor) ==========
  scrollToSlide(slideIndex) {
    var _a;
    if (!this.isInteractionReady()) this.prepareInteraction();
    if (this.emblaApi) {
      this.emblaApi.scrollTo(slideIndex);
      return;
    }
    if (Shopify.designMode && this.marquee) {
      this._editorMarqueePaused = true;
    }
    (_a = this.marquee) == null ? void 0 : _a.scrollTo(slideIndex);
  }
  pauseMarquee() {
    var _a;
    (_a = this.marquee) == null ? void 0 : _a.pause();
  }
  resumeMarquee() {
    var _a;
    this._editorMarqueePaused = false;
    (_a = this.marquee) == null ? void 0 : _a.resume();
  }
  resumeEditorMarquee() {
    this.resumeMarquee();
  }
  // ========== Marquee Mode ==========
  initMarquee(config) {
    var _a;
    const breakpoints = this.config.options.breakpoints || {};
    const breakpoint = isMobile() ? "(max-width: 991.98px)" : "(min-width: 992px)";
    const watchDrag = ((_a = breakpoints[breakpoint]) == null ? void 0 : _a.watchDrag) !== false;
    this.marquee = new EmblaMarqueeController({
      root: this,
      viewport: this.nodes.viewport,
      container: this.nodes.container,
      prefersReducedMotion,
      allowHoverPause: () => !isMobileAnim,
      sanitizeClone: (clone) => this._sanitizeMarqueeClone(clone),
      deferInit: true,
      manageFocus: true,
      nativeCursor: true
    });
    this.marquee.init({ ...config, watchDrag });
    if (this._editorMarqueePaused) this.marquee.pause();
  }
  _sanitizeMarqueeClone(clone) {
    this._resetClonedVideos(clone);
    Array.from(clone.children).forEach((item) => {
      if (item.dataset.delegatedAnim === "true") {
        const animTargets = item.matches(
          "anim-media, anim-element, anim-text, anim-divider"
        ) ? [item] : Array.from(
          item.querySelectorAll(
            "anim-media, anim-element, anim-text, anim-divider"
          )
        );
        animTargets.forEach((target) => {
          target.removeAttribute("style");
          Array.from(target.children).forEach(
            (child) => child.removeAttribute("style")
          );
          if (typeof target.reset === "function") target.reset();
        });
      } else {
        item.style.removeProperty("opacity");
        item.style.removeProperty("transform");
        item.style.removeProperty("will-change");
      }
    });
  }
  _resetClonedVideos(clone) {
    clone.querySelectorAll("custom-video").forEach((video) => {
      var _a, _b, _c, _d;
      const hasTemplate = Array.from(video.children).some(
        (child) => child.tagName === "TEMPLATE"
      );
      const media = Array.from(video.children).find(
        (child) => ["VIDEO", "IFRAME"].includes(child.tagName)
      );
      if (!hasTemplate && media) {
        const template = document.createElement("template");
        template.content.appendChild(media);
        video.appendChild(template);
      }
      video.classList.remove("loaded");
      video.removeAttribute("data-started");
      video.dataset.playbackActive = "true";
      const startsPlaying = video.dataset.autoplay === "true";
      (_a = video.querySelector(".vid-block-controls-play")) == null ? void 0 : _a.classList.toggle("show", !startsPlaying);
      (_b = video.querySelector(".vid-block-controls-pause")) == null ? void 0 : _b.classList.toggle("show", startsPlaying);
      (_c = video.querySelector("[data-video-controls-buttons]")) == null ? void 0 : _c.classList.remove("force-hide");
      (_d = Array.from(video.children).find(
        (child) => child.hasAttribute("data-video-cover")
      )) == null ? void 0 : _d.classList.remove("hidden");
    });
  }
  // ========== Resize Handling ==========
  onResize() {
    const newDeviceType = isMobile() ? "mobile" : "desktop";
    if (this.currentDeviceType !== newDeviceType) {
      this.currentDeviceType = newDeviceType;
      this.init();
    }
  }
  // ========== Grid Layout ==========
  flattenLayout() {
    flattenListingLayout(this);
  }
  applyGridLayout() {
    applyListingGridLayout(this);
  }
  // ========== Cleanup ==========
  registerCleanup(fn) {
    if (fn && typeof fn === "function") this.cleanupFunctions.push(fn);
  }
  destroy() {
    var _a;
    destroyListingEmbla(this);
    trimListingLoopClones(this);
    (_a = this.marquee) == null ? void 0 : _a.destroy();
    this.marquee = null;
  }
  // ========== Tab Group Animation API ==========
  _initStandalone() {
    if (this._reducedMotionQuery.matches || typeof isMobileAnim !== "undefined" && isMobileAnim || document.body.dataset.animationOnScroll !== "true") {
      if (typeof this.init === "function") this.init();
      return;
    }
    this._foldIO = new IntersectionObserver(
      (entries) => {
        this._foldIO.disconnect();
        this._foldIO = null;
        const isAboveFold = checkIsAboveFold(entries[0]);
        if (isAboveFold) {
          if (typeof this.init === "function") this.init();
          return;
        }
        if (!this.setup()) {
          if (this._isFirstInit && typeof this.init === "function") this.init();
          return;
        }
        this._prewarmObs = new IntersectionObserver(
          (entries2) => {
            if (entries2[0].isIntersecting) {
              this._prewarmObs.disconnect();
              this._prewarmObs = null;
              if (this._isFirstInit && typeof this.init === "function")
                this.init();
            }
          },
          { rootMargin: "0px 0px 50% 0px" }
        );
        this._prewarmObs.observe(this);
        this._scrollObs = new IntersectionObserver(
          (entries2) => {
            if (entries2[0].isIntersecting) {
              this._scrollObs.disconnect();
              this._scrollObs = null;
              this.play();
            }
          },
          { rootMargin: "0px 0px -10% 0px" }
        );
        this._scrollObs.observe(this);
      },
      { threshold: 0 }
    );
    this._foldIO.observe(this);
  }
  setup() {
    if (this._reducedMotionQuery.matches || isMobileAnim) return false;
    if (this._ready) return true;
    if (!canSetupListingReveal(this)) return false;
    const items = Array.from(this.querySelectorAll(".embla__slide > *"));
    items.forEach((item) => {
      const animTargets = item.matches(
        "anim-media, anim-element, anim-text, anim-divider"
      ) ? [item] : Array.from(
        item.querySelectorAll(
          "anim-media, anim-element, anim-text, anim-divider"
        )
      );
      const canDelegate = animTargets.length > 0 && animTargets.every(
        (target) => typeof target.setup === "function" && typeof target.play === "function"
      );
      if (canDelegate) {
        item.dataset.delegatedAnim = "true";
        animTargets.forEach((target) => target.setup());
      } else {
        delete item.dataset.delegatedAnim;
        item.style.willChange = "transform, opacity";
        item.style.opacity = "0";
        item.style.transform = "translateY(30px)";
      }
    });
    this._isPlaying = false;
    this._animItems = items;
    this._ready = true;
    return true;
  }
  play(delayMs = 0) {
    var _a;
    const wasFirstInit = this._isFirstInit;
    if (this.emblaApi && !wasFirstInit) {
      if (this._controller) {
        requestAnimationFrame(() => {
          if (this.emblaApi) this.emblaApi.reInit();
        });
      }
    }
    if (this._reducedMotionQuery.matches || isMobileAnim) {
      if (this._isFirstInit && typeof this.init === "function") this.init();
      return;
    }
    if (!ensureListingRevealReadyForPlay(this)) {
      if (this._isFirstInit && typeof this.init === "function") this.init();
      return;
    }
    const items = Array.from(this.querySelectorAll(".embla__slide > *"));
    if (items.length === 0) return;
    const firstItem = items[0];
    const isDelegated = firstItem.dataset.delegatedAnim === "true";
    if (!isDelegated && firstItem.style.opacity !== "0") {
      this._ready = false;
      return;
    }
    try {
      (_a = this._anim) == null ? void 0 : _a.revert();
    } catch (e) {
    }
    const baseDelay = delayMs || 0;
    const staggerAmount = 60;
    const duration = 600;
    const easing = "cubic-bezier(0.16, 1, 0.3, 1)";
    const maxDuration = duration + baseDelay + items.length * staggerAmount;
    clearTimeout(this._failsafeTimer);
    this._failsafeTimer = setTimeout(() => this.reset(), maxDuration + 200);
    this._isPlaying = true;
    const standardItems = [];
    items.forEach((item, i) => {
      if (item.dataset.delegatedAnim === "true") {
        const animTargets = item.matches(
          "anim-media, anim-element, anim-text, anim-divider"
        ) ? [item] : Array.from(
          item.querySelectorAll(
            "anim-media, anim-element, anim-text, anim-divider"
          )
        );
        animTargets.forEach((target) => {
          if (typeof target.play === "function")
            target.play(baseDelay + i * staggerAmount);
        });
      } else {
        standardItems.push({ item, index: i });
      }
    });
    if (standardItems.length > 0) {
      try {
        this._anim = waapi.animate(
          standardItems.map((s) => s.item),
          {
            opacity: [
              0,
              1
            ],
            transform: [
              "translateY(30px)",
              "translateY(0px)"
            ],
            duration,
            delay: (_, i) => baseDelay + standardItems[i].index * staggerAmount,
            ease: easing,
            composition: "replace",
            onComplete: () => {
              clearTimeout(this._failsafeTimer);
              this._failsafeTimer = null;
              this._anim = null;
              this._isPlaying = false;
              standardItems.forEach((s) => {
                s.item.style.removeProperty("opacity");
                s.item.style.removeProperty("transform");
                s.item.style.removeProperty("will-change");
              });
              if (this._isFirstInit && this.isConnected) this.init();
            }
          }
        );
      } catch (e) {
      }
    } else {
      clearTimeout(this._failsafeTimer);
      this._failsafeTimer = setTimeout(() => {
        this._failsafeTimer = null;
        this._isPlaying = false;
        if (this._isFirstInit && this.isConnected) this.init();
      }, maxDuration);
    }
    this._animItems = items;
  }
  reset() {
    var _a;
    if (this._reducedMotionQuery.matches || isMobileAnim) return;
    clearTimeout(this._failsafeTimer);
    try {
      (_a = this._anim) == null ? void 0 : _a.revert();
    } catch (e) {
    }
    this._anim = null;
    this._isPlaying = false;
    const items = this._animItems || Array.from(this.querySelectorAll(".embla__slide > *"));
    items.forEach((item) => {
      if (item.dataset.delegatedAnim === "true") {
        const animTargets = item.matches(
          "anim-media, anim-element, anim-text, anim-divider"
        ) ? [item] : Array.from(
          item.querySelectorAll(
            "anim-media, anim-element, anim-text, anim-divider"
          )
        );
        animTargets.forEach((target) => {
          if (typeof target.reset === "function") target.reset();
        });
      } else {
        item.style.removeProperty("opacity");
        item.style.removeProperty("transform");
        item.style.removeProperty("will-change");
      }
      delete item.dataset.delegatedAnim;
    });
    if (this._isFirstInit) {
      if (document.readyState === "complete") {
        if (typeof window.requestIdleCallback === "function") {
          requestIdleCallback(() => {
            if (this.isConnected) this.init();
          });
        } else {
          setTimeout(() => {
            if (this.isConnected) this.init();
          }, 100);
        }
      } else {
        window.addEventListener(
          "load",
          () => {
            if (typeof window.requestIdleCallback === "function") {
              requestIdleCallback(() => {
                if (this.isConnected) this.init();
              });
            } else {
              setTimeout(() => {
                if (this.isConnected) this.init();
              }, 100);
            }
          },
          { once: true }
        );
      }
    }
    this._ready = false;
  }
}
if (!customElements.get("listing-component")) {
  customElements.define("listing-component", ListingComponent);
}
